// Shape conversions between the WhatsApp calling settings form and the `calling_config` payload the
// API stores. Validation is not repeated here: the server normalizes the routing blob on save.

export const DAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const WEEKEND = ['SATURDAY', 'SUNDAY'];
const DEFAULT_OPEN_TIME = '09:00';
const DEFAULT_CLOSE_TIME = '17:00';
const DEFAULT_RING_TIMEOUT_SECONDS = 20;

export const ROUTING_TARGETS = [
  'assigned_agent',
  'conversation_participants',
  'conversation_team_members',
  'inbox_members',
  'admins_only',
  'account_broadcast',
  'reject_all',
];

export const RING_MODES = ['simultaneous', 'best_candidate_only', 'sequential'];
export const PRIORITIZATION_STRATEGIES = ['availability', 'fixed_order'];
export const CALL_ICON_VISIBILITIES = ['DEFAULT', 'DISABLE_ALL'];
export const CALLBACK_PERMISSION_STATUSES = ['ENABLED', 'DISABLED'];

// Meta stores times as "0900"; the form edits them as "09:00".
const toFormTime = (value, fallback) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 4) return fallback;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const defaultDaySlot = day => ({
  day,
  enabled: !WEEKEND.includes(day),
  openTime: DEFAULT_OPEN_TIME,
  closeTime: DEFAULT_CLOSE_TIME,
});

// A schedule that was saved is authoritative: a day missing from it is a day that is closed. Only
// an inbox that has never saved one starts from the Mon-Fri default.
export const buildScheduleForm = schedule => {
  if (!schedule?.length) return DAYS.map(defaultDaySlot);

  const stored = new Map(
    schedule.map(slot => [String(slot.day).toUpperCase(), slot])
  );

  return DAYS.map(day => {
    const slot = stored.get(day);

    return {
      day,
      enabled: Boolean(slot?.enabled),
      openTime: toFormTime(slot?.open_time, DEFAULT_OPEN_TIME),
      closeTime: toFormTime(slot?.close_time, DEFAULT_CLOSE_TIME),
    };
  });
};

export const buildHolidaysForm = holidays =>
  (holidays ?? []).map(holiday => ({
    date: holiday.date ?? '',
    name: holiday.name ?? '',
    openTime: toFormTime(holiday.open_time, DEFAULT_OPEN_TIME),
    closeTime: toFormTime(holiday.close_time, DEFAULT_CLOSE_TIME),
  }));

export const buildRoutingForm = routing => ({
  primaryTargets: routing?.primary_targets ?? ['assigned_agent'],
  fallbackTargets: routing?.fallback_targets ?? [],
  prioritization: routing?.prioritization?.strategy ?? 'availability',
  ringMode: routing?.ring_strategy?.mode ?? 'simultaneous',
  maxRecipients: Number(routing?.ring_strategy?.max_recipients ?? 0),
  ringTimeoutSeconds: Number(
    routing?.ring_timeout_seconds ?? DEFAULT_RING_TIMEOUT_SECONDS
  ),
  eligibility: {
    respectContactBlocklist:
      routing?.eligibility?.respect_contact_blocklist ?? true,
    onlyOnline: routing?.eligibility?.only_online ?? false,
    excludeBusy: routing?.eligibility?.exclude_busy ?? false,
    excludeAutoOffline: routing?.eligibility?.exclude_auto_offline ?? false,
    requireInboxAccess: routing?.eligibility?.require_inbox_access ?? true,
  },
});

export const buildCallingForm = (callingConfig = {}) => ({
  callIconVisibility: callingConfig.call_icon_visibility ?? 'DEFAULT',
  callbackPermissionStatus:
    callingConfig.callback_permission_status ?? 'ENABLED',
  countries: callingConfig.call_icons_countries ?? [],
  callHoursEnabled: Boolean(callingConfig.call_hours_enabled),
  timezone: callingConfig.call_hours_timezone || 'UTC',
  schedule: buildScheduleForm(callingConfig.call_hours_schedule),
  holidays: buildHolidaysForm(callingConfig.call_hours_holidays),
  routing: buildRoutingForm(callingConfig.inbound_call_routing),
});

export const buildCallingPayload = form => ({
  call_icon_visibility: form.callIconVisibility,
  callback_permission_status: form.callbackPermissionStatus,
  call_icons_countries: form.countries,
  call_hours_enabled: form.callHoursEnabled,
  call_hours_timezone: form.timezone,
  call_hours_schedule: form.schedule.map(slot => ({
    day: slot.day,
    enabled: slot.enabled,
    open_time: slot.openTime,
    close_time: slot.closeTime,
  })),
  call_hours_holidays: form.holidays
    .filter(holiday => holiday.date)
    .map(holiday => ({
      date: holiday.date,
      name: holiday.name,
      open_time: holiday.openTime,
      close_time: holiday.closeTime,
    })),
  inbound_call_routing: {
    primary_targets: form.routing.primaryTargets,
    fallback_targets: form.routing.fallbackTargets,
    ring_timeout_seconds: Number(form.routing.ringTimeoutSeconds),
    prioritization: { strategy: form.routing.prioritization },
    ring_strategy: {
      mode: form.routing.ringMode,
      max_recipients: Number(form.routing.maxRecipients),
    },
    eligibility: {
      respect_contact_blocklist:
        form.routing.eligibility.respectContactBlocklist,
      only_online: form.routing.eligibility.onlyOnline,
      exclude_busy: form.routing.eligibility.excludeBusy,
      exclude_auto_offline: form.routing.eligibility.excludeAutoOffline,
      require_inbox_access: form.routing.eligibility.requireInboxAccess,
    },
  },
});

// What Meta answers for /settings, folded into the same form shape so the two can be compared.
export const buildCallingFormFromMeta = (metaCalling = {}) => ({
  callIconVisibility: metaCalling.call_icon_visibility ?? 'DEFAULT',
  callbackPermissionStatus: metaCalling.callback_permission_status ?? 'ENABLED',
  countries: metaCalling.call_icons?.restrict_to_user_countries ?? [],
  callHoursEnabled: metaCalling.call_hours?.status === 'ENABLED',
  timezone: metaCalling.call_hours?.timezone_id || 'UTC',
  schedule: buildScheduleForm(
    (metaCalling.call_hours?.weekly_operating_hours ?? []).map(slot => ({
      day: slot.day_of_week,
      enabled: true,
      open_time: slot.open_time,
      close_time: slot.close_time,
    }))
  ),
  holidays: buildHolidaysForm(metaCalling.call_hours?.holiday_schedule),
});
